import React from 'react';
import { createPortal } from 'react-dom';
import type { PurchaseRequisition } from '../../types/purchasing';
import { formatCostObjectLabel } from '../../utils/purchasingSpendClass';
import { purchaseListStatus } from '../../utils/purchasingStatus';
import {
  lineFigures,
  requisitionListedTotal,
  requisitionTaxTotal,
  requisitionTotal,
} from '../../utils/purchasingLineItems';
import { additionalPurchaseLinks } from '../../utils/purchasingLinks';
import { issuedVendorGroups } from '../../utils/purchaseLineVendors';
import { purchasePurchaserDisplayName } from '../../utils/purchaseRequesterDisplay';
import type { PurchaseLineItem } from '../../types/purchasing';
import './PurchasePrintSheet.css';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

function formatDay(value: Date | undefined): string | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as unknown as string);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function qtyLabel(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (Number.isInteger(n)) return String(n);
  return String(n);
}

export function printPurchaseChecklist(documentTitle: string) {
  const previous = document.title;
  document.title = documentTitle;
  const restore = () => {
    document.title = previous;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
}

export function PurchasePrintSheet({
  row,
  requesterName,
}: {
  row: PurchaseRequisition;
  requesterName: string;
}) {
  if (typeof document === 'undefined') return null;

  const salesTaxPct = row.salesTaxPct ?? 0;
  const groups = issuedVendorGroups(row.lineItems, row.vendorName, row.poNumber);
  const issuedGroups = groups.filter(group => group.poNumber);
  const sheets = issuedGroups.length > 1
    ? issuedGroups
    : [{
        vendorName: groups[0]?.vendorName || row.vendorName || '',
        poNumber: groups[0]?.poNumber || row.poNumber || '',
        lines: row.lineItems,
      }];
  const status = purchaseListStatus(row);
  const created = formatDay(row.createdAt);
  const purchased = formatDay(row.purchasedAt);
  const printed = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const tag = row.badge
    || (row.origin === 'employee_recommendation' || row.source === 'crew_slack' ? 'Employee recommendation' : '');
  const extraLinks = additionalPurchaseLinks(row.links, row.lineItems);

  const sheet = (
    <div className="po-print-root" aria-hidden="true">
      {sheets.map(group => {
        const lines = group.lines as PurchaseLineItem[];
        const listed = requisitionListedTotal(lines);
        const tax = requisitionTaxTotal(lines, salesTaxPct);
        const computed = lines.length ? requisitionTotal(lines, salesTaxPct) : row.amount;
        const purchasedDiffers =
          sheets.length === 1 &&
          row.status === 'purchased' &&
          row.purchasedAmount != null &&
          Math.abs(row.purchasedAmount - computed) > 0.005;
        const isPo = !!group.poNumber;
        const meta: Array<[string, string]> = [
          ['Requested by', requesterName || row.requesterName || '—'],
          ['Vendor', group.vendorName || '—'],
          ['Job', formatCostObjectLabel(row.costObject) || '—'],
          ['Status', status.label],
        ];
  if (created) meta.push(['Created', created]);
  if (purchased) meta.push(['Purchased', purchased]);
  const purchasedBy = purchasePurchaserDisplayName(row);
  if (purchasedBy) meta.push(['Purchased by', purchasedBy]);
  if (row.paymentMethod) {
    meta.push([
      'Payment',
      row.paymentMethodNote ? `${row.paymentMethod} — ${row.paymentMethodNote}` : row.paymentMethod,
    ]);
  }
        if (tag) meta.push(['Tag', tag]);
        return (
      <article className="po-print-sheet" key={group.poNumber || row.prNumber}>
        <p className="po-print-kicker">Leiper&apos;s Creek Timber Frames</p>
        <h1 className="po-print-title">
          {isPo ? 'Purchase order' : 'Purchase requisition'}{' '}
          <span className="po-print-docno">{isPo ? group.poNumber : row.prNumber}</span>
        </h1>
        {isPo && <p className="po-print-sub">Requisition {row.prNumber}</p>}
        <h2 className="po-print-job">{row.title || 'Untitled purchase'}</h2>

        <dl className="po-print-meta">
          {meta.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        <table className="po-print-table">
          <thead>
            <tr>
              <th className="po-print-check-col" aria-label="Check" />
              <th>Item</th>
              <th className="num">Qty</th>
              <th className="num">Unit</th>
              <th className="num">Tax</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td />
                <td colSpan={5}>No line items recorded.</td>
              </tr>
            ) : (
              lines.map((line, index) => {
                const figures = lineFigures(line, salesTaxPct);
                const rate =
                  typeof line.taxRate === 'number' && Number.isFinite(line.taxRate)
                    ? line.taxRate
                    : line.taxable === false
                      ? null
                      : salesTaxPct;
                const url = (line.productUrl || '').trim();
                return (
                  <tr key={line.id || index}>
                    <td className="po-print-check-col">
                      <span className="po-print-box" />
                    </td>
                    <td>
                      <div className="po-print-desc">{line.description.trim() || 'Untitled item'}</div>
                      {line.productLabel &&
                        line.productLabel.trim() !== line.description.trim() &&
                        !/^product link$/i.test(line.productLabel.trim()) && (
                        <div className="po-print-note">{line.productLabel}</div>
                      )}
                      {line.hours != null && Number.isFinite(line.hours) && (
                        <div className="po-print-note">Hours {qtyLabel(line.hours)}</div>
                      )}
                      {url && <div className="po-print-url">{url}</div>}
                    </td>
                    <td className="num">{qtyLabel(line.quantity)}</td>
                    <td className="num">{money(line.unitPrice)}</td>
                    <td className="num">
                      {money(figures.tax)}
                      {rate != null && <span className="po-print-rate">{rate}%</span>}
                    </td>
                    <td className="num">{money(figures.amount)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div className="po-print-totals">
          <div>
            <span>Listed</span>
            <strong>{money(listed)}</strong>
          </div>
          <div>
            <span>Tax</span>
            <strong>{money(tax)}</strong>
          </div>
          <div className="po-print-grand">
            <span>Total</span>
            <strong>{money(computed)}</strong>
          </div>
          {purchasedDiffers && row.purchasedAmount != null && (
            <div>
              <span>Purchased</span>
              <strong>{money(row.purchasedAmount)}</strong>
            </div>
          )}
        </div>

        {row.justification.trim() && (
          <section className="po-print-block">
            <h3>Notes</h3>
            <p>{row.justification}</p>
          </section>
        )}

        {extraLinks.length > 0 && (
          <section className="po-print-block">
            <h3>Additional links</h3>
            <ul>
              {extraLinks.map(link => (
                <li key={link.id}>
                  {link.label ? <span>{link.label} — </span> : null}
                  {link.url}
                </li>
              ))}
            </ul>
          </section>
        )}

        {row.attachments.length > 0 && (
          <section className="po-print-block">
            <h3>Files</h3>
            <ul>
              {row.attachments.map(file => (
                <li key={file.storagePath || file.name}>
                  {file.name}
                  {file.kind ? ` (${file.kind})` : ''}
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="po-print-foot">
          Printed {printed}. Check each item as it is picked up or received.
        </p>
      </article>
        );
      })}
    </div>
  );

  return createPortal(sheet, document.body);
}
